import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo, SUBURBS } from "@/lib/site";
import { Reveal, MaskLines } from "@/components/Reveal";

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = SUBURBS.find((s) => s.slug === slug);
  const name = suburb?.name || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  useSeo(
    `Breeze Blocks ${name} — Supply & Install | Breeze Craft`,
    `Breeze block supply and installation in ${name}, Sydney. Feature walls, pool screens and front fences with free quotes and fast local delivery.`
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "suburb-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: `Breeze block supply and installation in ${name}`,
      provider: { "@type": "LocalBusiness", name: "Breeze Craft", telephone: "+61-404-678-321" },
      areaServed: { "@type": "Place", name: `${name}, Sydney, NSW` },
    });
    document.getElementById("suburb-schema")?.remove();
    document.head.appendChild(script);
    return () => document.getElementById("suburb-schema")?.remove();
  }, [name]);

  return (
    <div data-testid="suburb-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 sm:py-20">
        <p className="kicker mb-4">{suburb?.region || "Sydney"} · Service area</p>
        <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-ink" data-testid="suburb-heading">
          <MaskLines lines={[`Breeze blocks`, `${name}.`]} />
        </h1>
        <Reveal delay={0.2} className="mt-6 max-w-2xl">
          <p className="text-neutral-600 leading-relaxed">
            {suburb?.blurb || `We supply and install breeze blocks across ${name} and surrounding streets — feature walls, pool screens, front fences and commercial work.`}
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-8 flex flex-wrap gap-4">
          <Link to={`/quote?suburb=${encodeURIComponent(name)}`} data-testid="suburb-quote-button" className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors">
            Get a quote in {name} <ArrowRight size={16} />
          </Link>
          <Link to="/catalogue" data-testid="suburb-catalogue-link" className="inline-flex items-center gap-2 border border-ink px-7 py-3.5 text-sm font-medium text-ink hover:bg-ink hover:text-bone transition-colors">
            Browse blocks
          </Link>
        </Reveal>
      </section>

      <section className="border-y border-ink/10 bg-bone-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            ["Local delivery", `Pallet drops across ${name} on our twice-weekly ${suburb?.region || "Sydney"} run — crane truck or forklift ute depending on access.`],
            ["Install crews", `Our installers work in ${name} weekly. Most residential walls are measured, quoted and built within 2–4 weeks.`],
            ["Compliance aware", `Pool screens and boundary walls are built to your certifier's or engineer's requirements — we flag what's needed at quote stage.`],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.07}>
              <p className="font-display text-xl font-medium text-ink">{t}</p>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

    </div>
  );
}
